import React from 'react';
import { Link } from 'react-router-dom';
import { KeyIcon } from '@heroicons/react/24/outline';
import ConvertLogTab from '../components/converter/ConvertLogTab';
import TestApiKeyTab from '../components/converter/TestApiKeyTab';
import ConverterJobsDashboard from '../components/converter/ConverterJobsDashboard';
import { urlEnum, useUrlState } from '../hooks/useUrlState';
import { useConverterFeatures } from '../hooks/useFeatureStatus';

type TopTab = 'converter' | 'api';
type ApiSubTab = 'requests' | 'test';

const apiSubTabs: { id: ApiSubTab; label: string }[] = [
    { id: 'requests', label: 'Requests' },
    { id: 'test', label: 'Test API Key' },
];

const topTabs: { id: TopTab; label: string }[] = [
    { id: 'converter', label: 'Converter' },
    { id: 'api', label: 'API' },
];

const URL_SPEC = {
    tab: urlEnum<TopTab>(['converter', 'api'], 'converter'),
    sub: urlEnum<ApiSubTab>(['requests', 'test'], 'requests'),
};

function Converter() {
    const [state, setUrlState] = useUrlState(URL_SPEC);
    const { web, api } = useConverterFeatures();
    // Each top tab needs its own feature. The route gate ensures at least one is on.
    const visibleTabs = topTabs.filter((tab) => (tab.id === 'converter' ? web : api));
    const activeTab = visibleTabs.some((tab) => tab.id === state.tab) ? state.tab : visibleTabs[0]?.id;

    return (
        <div>
            <div className="border-b border-gray-700 mb-6 flex items-center justify-between">
                <nav className="flex space-x-8">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setUrlState({ tab: tab.id })}
                            className={`pb-3 px-1 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-b-2 border-primary text-primary'
                                    : 'text-secondary hover:text-body-color'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
                <span
                    className="bg-warning/20 text-warning px-2 py-0.5 mb-3 rounded text-xs font-bold uppercase tracking-wider border border-warning/40"
                    title="Converter is in beta"
                >
                    BETA
                </span>
            </div>

            {activeTab === 'converter' && <ConverterJobsDashboard urlPrefix="jobs" />}

            {activeTab === 'api' && (
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-heading">Alchemist Converter API</h1>
                        <Link to="/settings?tab=api-keys" className="btn-subtle text-sm flex items-center gap-2">
                            <KeyIcon className="w-4 h-4" />
                            Manage API Keys
                        </Link>
                    </div>
                    <div className="border-b border-gray-700 mb-6">
                        <nav className="flex space-x-8">
                            {apiSubTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setUrlState({ tab: 'api', sub: tab.id })}
                                    className={`pb-3 px-1 font-medium text-sm transition-colors ${
                                        state.sub === tab.id
                                            ? 'border-b-2 border-primary text-primary'
                                            : 'text-secondary hover:text-body-color'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                    <div>
                        {state.sub === 'requests' && <ConvertLogTab urlPrefix="requests" />}
                        {state.sub === 'test' && <TestApiKeyTab />}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Converter;
